import axios, { type AxiosInstance } from 'axios';
import type { TestEvent, SessionCreationQuotaResponse, QuarantineList } from '../types/index.js';
import type { QuarantineFetchOutcome } from '../reporter/test-controls.js';
import {
  ForbiddenAuthError,
  HttpDeliveryError,
  InvalidTokenTypeError,
  QuotaExceededError,
  QuotaExhaustedError,
  ServerEndpointError,
  UnauthorizedError,
} from '../reporter/errors.js';

export const AUTH_PATH = '/api/v1/reporter/auth';
export const QUARANTINE_PATH = '/api/v1/reporter/quarantine';
// Tests are already running while this is in flight; past this the build
// behaves as it would without Test Controls.
const QUARANTINE_TIMEOUT_MS = 5000;

const VALID_SASL_MECHANISMS = ['plain', 'scram-sha-256', 'scram-sha-512'] as const;
type SaslMechanism = (typeof VALID_SASL_MECHANISMS)[number];

export interface HttpClientOptions {
  token: string;
  serverUrl: string;
}

export interface RunLink {
  url: string;
  testRunId?: string;
}

export interface AuthResponse {
  projectId: string;
  kafka?: {
    brokers: string[];
    channel?: string;
    sasl?: {
      mechanism: SaslMechanism;
      username: string;
      password: string;
    };
    ssl?: boolean;
  };
  httpFallback?: string;
  /**
   * Advisory: when true the project is past its execution quota, so this run's
   * events will be blocked at ingestion. The reporter still sends run:begin
   * (which surfaces the blocked run on the dashboard) but stops streaming the
   * rest. Absent on older servers — treated as false.
   */
  overLimit?: boolean;
}

export class HttpClient {
  private client: AxiosInstance;

  constructor(options: HttpClientOptions) {
    this.client = axios.create({
      baseURL: options.serverUrl,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${options.token}`,
      },
      timeout: 10000,
    });
  }

  async authenticate(): Promise<AuthResponse> {
    try {
      const response = await this.client.post(AUTH_PATH);
      return normalizeAuthResponse(response.data);
    } catch (error) {
      if (axios.isAxiosError(error) && error.response) {
        const status = error.response.status;

        if (status === 402) {
          const data = error.response.data as Partial<SessionCreationQuotaResponse> | undefined;
          throw new QuotaExhaustedError(
            data?.message ?? 'Quota exhausted',
            data?.details ?? { planName: 'Unknown', totalLimit: 0, used: 0 }
          );
        }

        if (status === 403) {
          const data = error.response.data as { code?: string; message?: string } | undefined;
          if (data?.code === 'TOKEN_TYPE_NOT_ALLOWED') throw new InvalidTokenTypeError(data.message);
          throw new ForbiddenAuthError(data?.message);
        }

        if (status === 401) {
          const data = error.response.data as { message?: string } | undefined;
          throw new UnauthorizedError(data?.message);
        }

        if (status === 405) throw new ServerEndpointError(AUTH_PATH);
      }

      throw new Error(`Authentication failed: ${this.getErrorMessage(error)}`, { cause: error });
    }
  }

  async sendEvents(url: string, events: TestEvent[]): Promise<void> {
    try {
      await this.client.post(url, { events });
    } catch (error) {
      // Quota is permanent — do not retry.
      if (axios.isAxiosError(error) && error.response?.status === 402) {
        const data = error.response.data;
        const details = data?.details;
        if (data?.error === 'QUOTA_EXCEEDED' && details) {
          throw new QuotaExceededError(data.message || 'Quota exceeded', details);
        }
        throw new QuotaExhaustedError(
          data?.message ?? 'Quota exhausted',
          details ?? { planName: 'Unknown', totalLimit: 0, used: 0 }
        );
      }
      if (axios.isAxiosError(error) && error.response) {
        throw new HttpDeliveryError(error.response.status, this.getErrorMessage(error), { cause: error });
      }
      throw error;
    }
  }

  /** The project's Test Controls list. Never throws: a failure is a reason. */
  async fetchQuarantineList(): Promise<QuarantineFetchOutcome> {
    try {
      const response = await this.client.get(QUARANTINE_PATH, { timeout: QUARANTINE_TIMEOUT_MS });
      const list = normalizeQuarantineList(response.data);
      return list ? { list } : { reason: 'unreachable' };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.status === 404) return { reason: 'not_found' };
        if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') return { reason: 'timeout' };
      }
      return { reason: 'unreachable' };
    }
  }

  /** Returns null when the mapping is not recorded yet (404); throws otherwise. */
  async resolveRunLink(runId: string): Promise<RunLink | null> {
    try {
      const response = await this.client.get(runLinkPath(runId));
      const data = response.data as { url?: unknown; test_run_id?: unknown } | undefined;
      const url = data?.url;
      if (typeof url !== 'string' || url.length === 0) return null;
      const testRunId = data?.test_run_id;
      return typeof testRunId === 'string' && testRunId.length > 0 ? { url, testRunId } : { url };
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) return null;
      // Carries the status so the caller can tell a blip from a refusal.
      if (axios.isAxiosError(error) && error.response) {
        throw new HttpDeliveryError(error.response.status, this.getErrorMessage(error), { cause: error });
      }
      throw error;
    }
  }

  private getErrorMessage(error: unknown): string {
    if (axios.isAxiosError(error)) return error.response?.data?.message || error.message;
    if (error instanceof Error) return error.message;
    return String(error);
  }
}

function runLinkPath(runId: string): string {
  return `/api/v1/reporter/runs/${encodeURIComponent(runId)}/link`;
}

// Server emits SASL mechanism uppercase; KafkaJS demands lowercase.
function normalizeAuthResponse(raw: unknown): AuthResponse {
  const data = (raw ?? {}) as AuthResponse;
  if (!data.kafka?.sasl) return data;
  const lowered = data.kafka.sasl.mechanism.toLowerCase();
  if (!(VALID_SASL_MECHANISMS as readonly string[]).includes(lowered)) {
    throw new Error(`Unsupported SASL mechanism from server: ${data.kafka.sasl.mechanism}`);
  }
  return {
    ...data,
    kafka: {
      ...data.kafka,
      sasl: { ...data.kafka.sasl, mechanism: lowered as SaslMechanism },
    },
  };
}

// A body without the list is not a list: recorded as not applied, never as an
// applied empty one.
function normalizeQuarantineList(raw: unknown): QuarantineList | null {
  const data = (raw ?? {}) as Partial<QuarantineList>;
  if (!Array.isArray(data.quarantined)) return null;
  const ids = (value: unknown): string[] =>
    Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
  const rules = ruleCounts(data.rules);
  return {
    quarantined: ids(data.quarantined),
    critical: ids(data.critical),
    knownErrors: Array.isArray(data.knownErrors)
      ? data.knownErrors
          .filter((k) => k && typeof k.pattern === 'string')
          .map((k) => ({ testIds: ids(k.testIds), pattern: k.pattern }))
      : [],
    ...(typeof data.generatedAt === 'string' && { generatedAt: data.generatedAt }),
    ...(rules && { rules }),
  };
}

function ruleCounts(raw: unknown): QuarantineList['rules'] | undefined {
  const r = (raw ?? {}) as Record<string, unknown>;
  const n = (v: unknown): v is number => typeof v === 'number' && Number.isInteger(v) && v >= 0;
  return n(r.quarantined) && n(r.knownErrors) && n(r.critical)
    ? { quarantined: r.quarantined, knownErrors: r.knownErrors, critical: r.critical }
    : undefined;
}
